<script lang="ts">
	import { goto, replaceState } from '$app/navigation';
	import { page } from '$app/state';
	import BlogShareModule from '$lib/storyblok/blocks/blogShareModule.svelte';
	import { getBlogArticleSubHeading } from '$lib/storyblok/blogHelpers';
	import Blok from '$lib/storyblok/blok.svelte';
	import BlogBreadcrumbs from '$lib/storyblok/components/blogBreadcrumbs.svelte';
	import DesktopPageGrid from '$lib/storyblok/components/desktopPageGrid.svelte';
	import Image from '$lib/storyblok/components/image.svelte';
	import PageGrid from '$lib/storyblok/components/pageGrid.svelte';
	import { editable } from '$lib/storyblok/editable.js';
	import type { SbBlogArticleCategoryStory } from '$lib/storyblok/types.js';
	import { useStoryblokBridge } from '@storyblok/js';
	import { tick } from 'svelte';

	let { data } = $props();

	let category = $derived(data.story.content.category);

	async function selectCategory(category: SbBlogArticleCategoryStory | null) {
		await tick();
		replaceState('', { ...page.state, selectedBlogArticleCategory: category });
	}

	$effect.pre(() => {
		if (category?.uuid !== page.state.selectedBlogArticleCategory?.uuid) {
			selectCategory(category);
		}

		useStoryblokBridge(data.story.id, (newStory) => (data.story = newStory), {
			resolveRelations: ['blog_page.category']
		});
	});

	const shareBlok = { label: 'Share this article:' };
</script>

<div class="mb-48">
	<PageGrid class="mb-5 mt-2 lg:mb-6 lg:mt-7">
		<BlogBreadcrumbs
			pageName={data.story.content.title}
			onselectCategory={() => {
				if (page.route.id !== '/blog') {
					const searchParams = new URLSearchParams(window.location.search);
					if (category) {
						searchParams.set('category', category.slug);
					}

					goto(`/blog?${searchParams}`, { state: page.state });
					return;
				}
			}}
		/>
	</PageGrid>
	<article use:editable={data.story.content}>
		<div class="mb-5 flex flex-col gap-y-6">
			<DesktopPageGrid>
				{#key data.story.content.hero_image?.filename}
					<Image
						wrapperClass="col-span-full lg:col-start-2 lg:col-end-12"
						class="aspect-16/9 w-full bg-stone-4 object-cover object-center lg:rounded-2xl"
						src={data.story.content.hero_image?.filename ?? ''}
						alt={data.story.content.hero_image?.alt ?? ''}
						title={data.story.content.hero_image?.title ?? ''}
						width={780}
						sizes={{ 1024: { width: 1924 } }}
						loading="eager"
						priority="high"
					/>
				{/key}
			</DesktopPageGrid>
			<PageGrid>
				<div class="col-span-full flex items-center justify-between lg:col-start-4 lg:col-end-10">
					{#if category}
						<div
							class="bg-{category.content.list_badge_color} text-{category.content
								.list_text_color} rounded-[4px] p-2 text-[14px]/[140%] font-semibold lg:px-3 lg:text-[18px]/[110%]"
						>
							{category.content.name}
						</div>
					{/if}
					<div class="text-[14px]/[140%] text-brown-3 lg:text-[16px]/[110%]">
						{getBlogArticleSubHeading(data.story)}
					</div>
				</div>
			</PageGrid>
		</div>

		<PageGrid>
			<h1
				class="col-span-full mb-5 text-[32px]/[110%] font-semibold tracking-[-0.32px] text-brown-2 lg:col-start-4 lg:col-end-10 lg:mb-8 lg:text-[56px]/[110%] lg:tracking-[-0.56px]"
			>
				{data.story.content.title}
			</h1>
		</PageGrid>

		<BlogShareModule content={shareBlok} />
		<Blok content={data.story.content} />
		<BlogShareModule content={shareBlok} />
	</article>
</div>
